import { Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { DatepickerHelper } from '../../../../shared/validators/date-picker-form-validators';
import { setControlDisabled } from '../../../../shared/utils/set-control-disabled';
import { Document } from '@models/documents/document';
import { WithdrawLodgedDocumentStepsEnum } from './WithdrawLodgedDocumentStepsEnum';
import { SelectOption } from '@models/ui-models/select-options';
import {
  asicDocumentNumberValidators,
  documentNumberCustomError
} from '../../../../shared/validators/document-number.validator';
import { DatePickerComponent } from '@shared/controls/date-picker/date-picker.component';
import { InputComponent } from '@shared/controls/input/input.component';
import { SelectComponent } from '@shared/controls/select/select.component';
import { TextareaComponent } from '@shared/controls/textarea/textarea.component';
import {
  CompanyWithdrawLodgedDocumentChange,
  DocumentWithdrawReason,
  DocumentWithdrawReasonOptions,
} from '../../../models/company/company-withdraw-lodged-document-change.model';
import { BaseStepperFormComponent, StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";

@Component({
  selector: 'app-form106-withdraw-lodged-document',
  templateUrl: './company-withdraw-lodged-document-change.component.html',
  styleUrl: './company-withdraw-lodged-document-change.component.scss',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    StepperFormComponent,
    StepperFormDescriptionComponent,
    InputComponent,
    DatePickerComponent,
    SelectComponent,
    TextareaComponent
  ]
})
export class CompanyWithdrawLodgedDocumentChangeComponent extends BaseStepperFormComponent<WithdrawLodgedDocumentStepsEnum, CompanyWithdrawLodgedDocumentChange> {
  WithdrawLodgedDocumentStepsEnum = WithdrawLodgedDocumentStepsEnum;
  DocumentWithdrawReason = DocumentWithdrawReason;

  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;
  readonly documentWithdrawReasonOptions: SelectOption[] = DocumentWithdrawReasonOptions;
  readonly documentNumberCustomError = documentNumberCustomError;

  override stepperForm = new FormGroup({
    [WithdrawLodgedDocumentStepsEnum.FormDetails]: new FormGroup({}),
    [WithdrawLodgedDocumentStepsEnum.DocumentDetails]: new FormGroup({
      documentNumber: new FormControl<string>('', asicDocumentNumberValidators()),
      documentLodgementDate: new FormControl<Date | null>(null, [Validators.required]),
    }),
    [WithdrawLodgedDocumentStepsEnum.AmendmentDetails]: new FormGroup({
      reason: new FormControl<DocumentWithdrawReason | null>(null, [Validators.required]),
      duplicateDocumentNumber: new FormControl<string>('', asicDocumentNumberValidators()),
      withdrawalReasonDetails: new FormControl<string>('', [Validators.required]),
    }),
  });

  constructor() {
    super();
    this.setupSteps(WithdrawLodgedDocumentStepsEnum);
    this.redirectAfterSubmit = true;
  }

  ngOnInit(): void {
    this.minDate = DatepickerHelper.getStructFromDate(DatepickerHelper.getDateOf1990January());
    this.maxDate = DatepickerHelper.getNextNYearsStruct(10);
    this.companyDetailsForm.controls.documentNumber.setValue(this.formModel.documentNumber ?? null);
    this.companyDetailsForm.controls.documentLodgementDate.setValue(DatepickerHelper.getToday());


    this.listenDocumentWithdrawReasonChange();
    this.setupChange();
  }

  override setupChange(): void {
    if (!this.isEdit) {
      return;
    }

    this.companyDetailsForm.patchValue(this.formModel);
    this.amendmentDetailsForm.patchValue({
      ...this.formModel as any,
    });
  }

  override buildDocument(): Document | null {
    const documentLodgementDate = this.companyDetailsForm.value.documentLodgementDate as Date | string;
    const date = documentLodgementDate instanceof Date ? documentLodgementDate : new Date(documentLodgementDate);

    const change = new CompanyWithdrawLodgedDocumentChange({
      ...this.amendmentDetailsForm.value as Partial<CompanyWithdrawLodgedDocumentChange>,
      ...this.companyDetailsForm.value as Partial<CompanyWithdrawLodgedDocumentChange>,
      documentLodgementDate: DatepickerHelper.buildDateString(date),
      changeDate: DatepickerHelper.buildTodayDateString(),
      reason: this.amendmentDetailsForm.value.reason as DocumentWithdrawReason,
    });

    try {
      return new Document({
        changes: [change],
        type: 'c:106',
        entityId: this.companyChangeData.entityId,
        documentId: this.companyChangeData?.documentId,
      });
    } catch (error) {
      console.warn(error);
      return null;
    }
  }

  override afterSubmit(): void {
    this.amendmentDetailsForm.controls.reason.patchValue(this.amendmentDetailsForm.controls.reason.value ?? null);
  }

  private listenDocumentWithdrawReasonChange(): void {
    this.amendmentDetailsForm.controls.reason.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reason) => {
        setControlDisabled(this.amendmentDetailsForm.controls.withdrawalReasonDetails, reason == DocumentWithdrawReason.Duplicated);
        setControlDisabled(this.amendmentDetailsForm.controls.duplicateDocumentNumber, reason == DocumentWithdrawReason.Incorrect);
      });
  }

  get companyDetailsForm() {
    return this.stepperForm.controls[WithdrawLodgedDocumentStepsEnum.DocumentDetails];
  }

  get amendmentDetailsForm() {
    return this.stepperForm.controls[WithdrawLodgedDocumentStepsEnum.AmendmentDetails];
  }
}
